/* eslint-disable max-len */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Box } from '@mui/material';
import { UserContext } from '../../contexts/User';
import { NotificationContext } from '../../contexts/Notification';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';

// In-app alert notifications for this member. Keys match
// server/constants/notifications.js ALERT_PREFERENCE_TYPES; all default to on.
const NOTIFICATION_OPTIONS = [
  { type: 'hr_bot_alert_self', label: 'HR Bot alerts about this member' },
  { type: 'hr_bot_alert_subordinate', label: 'HR Bot alerts about their subordinates (as manager)' },
  { type: 'schedule_adherence_self', label: 'Schedule Adherence alerts about this member' },
  { type: 'schedule_adherence_subordinate', label: 'Schedule Adherence alerts about their subordinates (as manager)' },
];
const NOTIFICATION_TYPES = NOTIFICATION_OPTIONS.map((o) => o.type);
const allNotificationsOn = () => Object.fromEntries(NOTIFICATION_TYPES.map((t) => [t, true]));

function CheckRow({
  checked, onChange, label, disabled,
}) {
  return (
    <label className="flex items-center gap-2.5 cursor-pointer py-1.5">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className="w-4 h-4 accent-[#0e1e40]"
      />
      <span className="text-sm text-ink-900">{label}</span>
    </label>
  );
}
CheckRow.propTypes = {
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
};
CheckRow.defaultProps = {
  disabled: false,
};

function SetupAlertModal({ closeModal, member, getMembers }) {
  const { activeWorkspace } = useContext(UserContext);
  const { displayNotification } = useContext(NotificationContext);
  const [autoSendHR, setAutoSendHR] = useState(member.autoSendHR);
  const [autoSendIT, setAutoSendIT] = useState(member.autoSendIT);
  const [autoSendInfoSec, setAutoSendInfoSec] = useState(member.autoSendInfoSec);
  const [scheduleAdherence, setScheduleAdherence] = useState(!!member.scheduleAdherence);
  const [notifications, setNotifications] = useState(allNotificationsOn);
  const [notificationsLoaded, setNotificationsLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const response = await sendRequest({
        method: 'get',
        url: API.WORKSPACE.setupAlert(activeWorkspace.id, member.id),
      });
      if (cancelled) return;
      if (response.success) {
        const prefs = response.data?.notificationPreferences || {};
        setNotifications(Object.fromEntries(NOTIFICATION_TYPES.map((t) => [t, prefs[t]?.inApp !== false])));
        setNotificationsLoaded(true);
      } else {
        displayNotification({ message: 'Could not load notification settings for this member.', severity: 'error' });
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWorkspace?.id, member.id]);

  const handleSave = async () => {
    const response = await sendRequest({
      method: 'put',
      url: API.WORKSPACE.setupAlert(activeWorkspace.id, member.id),
      data: {
        autoSendHR,
        autoSendIT,
        autoSendInfoSec,
        scheduleAdherence,
        // Only sent once loaded, so a failed load cannot overwrite saved settings.
        ...(notificationsLoaded && {
          notificationPreferences: Object.fromEntries(
            NOTIFICATION_TYPES.map((t) => [t, { inApp: notifications[t] }]),
          ),
        }),
      },
    });
    if (response.success) {
      displayNotification({ message: 'Alert preferences saved', severity: 'success' });
      getMembers();
    } else {
      displayNotification({ message: 'Failed to save alert preferences. Please try again.', severity: 'error' });
    }
    closeModal();
  };

  const allChecked = autoSendHR && autoSendIT && autoSendInfoSec && scheduleAdherence;

  return (
    <Modal open onClose={closeModal} className="flex-row align-c jstfy-c" data-testid="setup-alert-modal">
      <Box style={{
        width: 480,
        maxWidth: 'calc(100% - 32px)',
        padding: 24,
        background: '#fff',
        borderRadius: 12,
      }}
      >
        <h5 className="text-lg font-semibold leading-none text-ink-900 mb-5" data-testid="modal-title">
          Setup Alert Acknowledge For Member
        </h5>

        <div className="flex flex-col">
          <CheckRow
            checked={allChecked}
            onChange={(e) => {
              setAutoSendHR(e.target.checked);
              setAutoSendIT(e.target.checked);
              setAutoSendInfoSec(e.target.checked);
              setScheduleAdherence(e.target.checked);
            }}
            label="Select All"
          />
          <div className="my-1" style={{ borderTop: '1px solid #e0e1e6' }} />
          <CheckRow checked={autoSendHR} onChange={(e) => setAutoSendHR(e.target.checked)} label="HR Bot Alert" />
          <CheckRow checked={autoSendIT} onChange={(e) => setAutoSendIT(e.target.checked)} label="IT Bot Alert" />
          <CheckRow checked={autoSendInfoSec} onChange={(e) => setAutoSendInfoSec(e.target.checked)} label="InfoSec Bot Alert" />
          <CheckRow checked={scheduleAdherence} onChange={(e) => setScheduleAdherence(e.target.checked)} label="Schedule Adherence (HR Bot)" />
        </div>

        <div className="flex flex-col mt-4 pt-3" style={{ borderTop: '1px solid #e0e1e6' }} data-testid="alert-notifications">
          <span className="text-xs font-semibold uppercase tracking-wide text-ink-500 mb-1">
            In-app notifications
          </span>
          {NOTIFICATION_OPTIONS.map((option) => (
            <CheckRow
              key={option.type}
              checked={notifications[option.type]}
              onChange={(e) => setNotifications((prev) => ({ ...prev, [option.type]: e.target.checked }))}
              label={option.label}
              disabled={!notificationsLoaded}
            />
          ))}
        </div>

        <div className="flex justify-start items-center gap-2 mt-6">
          <button
            type="button"
            onClick={closeModal}
            className="inline-flex items-center justify-center px-4 py-2 border border-rule rounded-[8px] text-sm font-medium text-ink-900 bg-white hover:bg-gray-50 transition"
            style={{ borderStyle: 'solid' }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="inline-flex items-center justify-center px-4 py-2 bg-[#53A551] text-white rounded-[8px] text-sm font-semibold hover:bg-[#458a44] transition"
          >
            Ok
          </button>
        </div>
      </Box>
    </Modal>
  );
}

SetupAlertModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  getMembers: PropTypes.func.isRequired,
  member: PropTypes.shape({
    id: PropTypes.number.isRequired,
    autoSendHR: PropTypes.bool.isRequired,
    autoSendIT: PropTypes.bool.isRequired,
    autoSendInfoSec: PropTypes.bool.isRequired,
    scheduleAdherence: PropTypes.bool,
  }).isRequired,
};

export default SetupAlertModal;
